import { useCallback, useEffect, useState } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import type {
  AbgleichAntwort, AbgleichArmatur, AbgleichPruefung, AbgleichVentil, ApiClient, KaeltenachweisAnnahme,
  KaeltenachweisBefund, KaeltenachweisNormverweis,
} from '../api';
import { PageHeader } from '../rahmen/PageHeader';
import {
  ContentSection, DataTable, EmptyState, ErrorState, ExportLeiste, KpiCard, KpiReihe, LoadingState,
  StatusBadge, Vorbehalte, annahmenVorbehalt, formatiereZahl, type Spalte,
} from '../muster';
import {
  ARMATUR, BEREICHE, BEREICH_BESCHREIBUNG, BEREICH_TITEL, EINSTELLART, einstellwert, gruppen, gruppeVon, kpa,
  nachkomma, rechenstandAbgleich, untereSchranke, zustandErgebnis, zustandHerkunftNachweis, type Gruppenzeile,
} from './heizung/abgleich';

/**
 * Der hydraulische Abgleich – DIN EN 14336 / VdZ-Verfahren B (TH-4, KG 420).
 *
 * ═══ Was die Seite zeigt ══════════════════════════════════════════════════
 *
 * Den jüngsten beendeten Lauf von `heizung-abgleich`: die Einstellwerteliste
 * je Verbraucher (Voreinstellung am Heizkörperventil, kv-Wert am
 * Abgleichventil der Geräte), die vorgeschlagenen Strangregulierventile und
 * Differenzdruckregler, die Pumpen nach dem Abgleich und ihren EEI. Die Seite
 * rechnet nichts; Bericht HZG-ABG und Excel-Einstellwerteliste lesen
 * dasselbe Datenmodell.
 *
 * ═══ Warum fast alles „vorläufig“ heißt ═══════════════════════════════════
 *
 * Ein Ergebnis heißt nur „erfüllt“, wenn alle genutzten Eingaben bestätigt
 * sind: Mindestdruck und Geräuschgrenze der Ventile, Schwellen der
 * Strangarmaturen, Rundungsregel, Heizlastlauf (HZK-36, HZK-42 bis HZK-47).
 * Die Netze sind im Modell unvollständig (HZK-25) – wo Verbraucher ohne Weg
 * sind, steht „≥“ an der Förderhöhe.
 *
 * TX-STANDARD S1: PageHeader mit Rechenstand, Vorbehalte, höchstens sechs
 * Kennzahlen, DataTable, Fachstatus S1.5, Annahmenliste S1.6, Normbezug.
 * Die Exportleiste (PDF HZG-ABG, Excel) folgt mit TX1/TX2.
 */

interface Rahmen { api: ApiClient; projekt: { id: string } }

function Ergebnis({ e }: { e: string }) {
  const s = zustandErgebnis(e);
  return <StatusBadge klein zustand={s.zustand} text={s.text} />;
}

const VENTILE: Spalte<AbgleichVentil>[] = [
  { schluessel: 'gruppe', titel: 'Gruppe', wert: (v) => v.systemgruppe },
  { schluessel: 'verbraucher', titel: 'Verbraucher', immerSichtbar: true,
    wert: (v) => v.bezeichnung ?? '', inhalt: (v) => (v.istMassgebend ? <strong>{v.bezeichnung} (maßgebend)</strong> : v.bezeichnung ?? '—') },
  { schluessel: 'raum', titel: 'Raum', wert: (v) => v.raumnummer ?? '' },
  { schluessel: 'element', titel: 'Element', wert: (v) => v.elementId, versteckt: true },
  { schluessel: 'vs', titel: 'Volumenstrom', einheit: 'm³/h', art: 'zahl', nachkomma: 3, wert: (v) => v.volumenstromM3h },
  { schluessel: 'weg', titel: 'Δp Weg', einheit: 'kPa', art: 'zahl', nachkomma: 1, wert: (v) => kpa(v.wegPa) },
  { schluessel: 'abgleich', titel: 'Abgleichdruck', einheit: 'kPa', art: 'zahl', nachkomma: 1, wert: (v) => kpa(v.abgleichPa) },
  { schluessel: 'armaturen', titel: 'Armaturen', wert: (v) => v.armaturen ?? '' },
  { schluessel: 'rest', titel: 'Rest am Ventil', einheit: 'kPa', art: 'zahl', nachkomma: 1, wert: (v) => kpa(v.restPa) },
  { schluessel: 'ventil', titel: 'Δp Ventil', einheit: 'kPa', art: 'zahl', nachkomma: 1, wert: (v) => kpa(v.ventilSollPa) },
  { schluessel: 'kv', titel: 'kv erforderlich', einheit: 'm³/h', art: 'zahl', nachkomma: 3, wert: (v) => v.kvErforderlich },
  { schluessel: 'einstellung', titel: 'Einstellung', wert: (v) => einstellwert(v, formatiereZahl) },
  { schluessel: 'art', titel: 'Art', wert: (v) => EINSTELLART[v.einstellart] ?? v.einstellart, versteckt: true },
  { schluessel: 'baureihe', titel: 'Baureihe', wert: (v) => v.baureihe ?? '' },
  { schluessel: 'autoritaet', titel: 'Autorität', art: 'zahl', nachkomma: 2, wert: (v) => v.autoritaet },
  { schluessel: 'ergebnis', titel: 'Ergebnis', art: 'status',
    wert: (v) => zustandErgebnis(v.ergebnis).text, inhalt: (v) => <Ergebnis e={v.ergebnis} /> },
  { schluessel: 'hinweis', titel: 'Hinweis', wert: (v) => v.hinweis ?? '', versteckt: true },
];

const ARMATUREN: Spalte<AbgleichArmatur>[] = [
  { schluessel: 'kennung', titel: 'Kennung', immerSichtbar: true, wert: (a) => a.kennung },
  { schluessel: 'art', titel: 'Art', wert: (a) => ARMATUR[a.art] ?? a.art },
  { schluessel: 'ebene', titel: 'Ebene', art: 'zahl', nachkomma: 0, wert: (a) => a.ebene },
  { schluessel: 'vorlauf', titel: 'Vorlauf-Element', wert: (a) => a.vorlaufElementId ?? '' },
  { schluessel: 'ruecklauf', titel: 'Rücklauf-Element', wert: (a) => a.ruecklaufElementId ?? '' },
  { schluessel: 'verbraucher', titel: 'Verbraucher', art: 'zahl', nachkomma: 0, wert: (a) => a.verbraucher },
  { schluessel: 'vs', titel: 'Volumenstrom', einheit: 'm³/h', art: 'zahl', nachkomma: 2, wert: (a) => a.volumenstromM3h },
  { schluessel: 'drosselung', titel: 'Drosselung', einheit: 'kPa', art: 'zahl', nachkomma: 1, wert: (a) => kpa(a.drosselungPa) },
  { schluessel: 'kv', titel: 'kv erforderlich', einheit: 'm³/h', art: 'zahl', nachkomma: 2, wert: (a) => a.kvErforderlich },
  { schluessel: 'sollwert', titel: 'Sollwert Δp-Regler', einheit: 'kPa', art: 'zahl', nachkomma: 1, wert: (a) => kpa(a.sollwertPa) },
  { schluessel: 'geschosse', titel: 'Geschosse', wert: (a) => a.geschosse ?? '' },
  { schluessel: 'ergebnis', titel: 'Ergebnis', art: 'status',
    wert: (a) => zustandErgebnis(a.ergebnis).text, inhalt: (a) => <Ergebnis e={a.ergebnis} /> },
  { schluessel: 'hinweis', titel: 'Hinweis', wert: (a) => a.hinweis ?? '', versteckt: true },
];

const GRUPPEN: Spalte<Gruppenzeile>[] = [
  { schluessel: 'gruppe', titel: 'Gruppe', immerSichtbar: true, wert: (g) => g.gruppe },
  { schluessel: 'verbraucher', titel: 'Verbraucher', art: 'zahl', nachkomma: 0, wert: (g) => g.verbraucher },
  { schluessel: 'mitWert', titel: 'mit Einstellwert', art: 'zahl', nachkomma: 0, wert: (g) => g.mitEinstellwert },
  { schluessel: 'ohneWeg', titel: 'ohne Weg', art: 'zahl', nachkomma: 0, wert: (g) => g.ohneWeg },
  { schluessel: 'nicht', titel: 'nicht einstellbar', art: 'zahl', nachkomma: 0, wert: (g) => g.nichtEinstellbar },
  { schluessel: 'armaturen', titel: 'Strangarmaturen', art: 'zahl', nachkomma: 0, wert: (g) => g.armaturen },
  { schluessel: 'regler', titel: 'davon Δp-Regler', art: 'zahl', nachkomma: 0, wert: (g) => g.regler },
];

function pruefspalten(schranke: (p: AbgleichPruefung) => boolean): Spalte<AbgleichPruefung>[] {
  return [
    { schluessel: 'gegenstand', titel: 'Gegenstand', immerSichtbar: true, wert: (p) => p.gegenstand },
    { schluessel: 'pruefung', titel: 'Prüfung', wert: (p) => p.bezeichnung },
    { schluessel: 'wert', titel: 'Wert', art: 'zahl', wert: (p) => p.wert,
      inhalt: (p) => `${schranke(p) && p.wert !== null ? '≥ ' : ''}${formatiereZahl(p.wert, nachkomma(p.einheit))}` },
    { schluessel: 'grenze', titel: 'Grenze', art: 'zahl', wert: (p) => p.grenzwert,
      inhalt: (p) => formatiereZahl(p.grenzwert, nachkomma(p.einheit)) },
    { schluessel: 'einheit', titel: 'Einheit', wert: (p) => p.einheit ?? '' },
    { schluessel: 'ergebnis', titel: 'Ergebnis', art: 'status',
      wert: (p) => zustandErgebnis(p.ergebnis).text, inhalt: (p) => <Ergebnis e={p.ergebnis} /> },
    { schluessel: 'fachfrage', titel: 'Fachfrage', wert: (p) => p.fachfrage ?? '' },
    { schluessel: 'rechenweg', titel: 'Rechenweg', wert: (p) => p.rechenweg, versteckt: true },
  ];
}

const ANNAHMEN: Spalte<KaeltenachweisAnnahme>[] = [
  { schluessel: 'parameter', titel: 'Parameter', immerSichtbar: true, wert: (a) => a.parameter },
  { schluessel: 'wert', titel: 'Wert', wert: (a) => a.wert ?? 'offen' },
  { schluessel: 'einheit', titel: 'Einheit', wert: (a) => a.einheit ?? '' },
  { schluessel: 'herkunft', titel: 'Herkunft', art: 'status',
    wert: (a) => zustandHerkunftNachweis(a.herkunft).text,
    inhalt: (a) => { const s = zustandHerkunftNachweis(a.herkunft); return <StatusBadge klein zustand={s.zustand} text={s.text} />; } },
  { schluessel: 'quelle', titel: 'Quelle/Norm', wert: (a) => [a.quelle, a.normbezug].filter(Boolean).join(' · ') },
  { schluessel: 'bestaetigt', titel: 'Bestätigung', art: 'status',
    wert: (a) => (a.bestaetigt ? 'bestätigt' : 'unbestätigt'),
    inhalt: (a) => (
      <StatusBadge klein zustand={a.bestaetigt ? 'gut' : 'warnung'} text={a.bestaetigt ? 'bestätigt' : 'unbestätigt'} />
    ) },
];

const BEFUNDE: Spalte<KaeltenachweisBefund>[] = [
  { schluessel: 'schwere', titel: 'Schwere', art: 'status',
    wert: (b) => (b.schwere === 'F' ? 'Fehler' : b.schwere === 'W' ? 'Warnung' : 'Hinweis'),
    inhalt: (b) => (
      <StatusBadge klein zustand={b.schwere === 'F' ? 'fehler' : b.schwere === 'W' ? 'warnung' : 'info'}
        text={b.schwere === 'F' ? 'Fehler' : b.schwere === 'W' ? 'Warnung' : 'Hinweis'} />
    ) },
  { schluessel: 'regel', titel: 'Regel', wert: (b) => b.regel },
  { schluessel: 'gegenstand', titel: 'Gegenstand', wert: (b) => b.gegenstand },
  { schluessel: 'meldung', titel: 'Meldung', wert: (b) => b.meldung },
];

const NORMEN: Spalte<KaeltenachweisNormverweis>[] = [
  { schluessel: 'norm', titel: 'Norm', immerSichtbar: true, wert: (n) => n.norm },
  { schluessel: 'ausgabe', titel: 'Ausgabe', wert: (n) => n.ausgabe ?? '—' },
  { schluessel: 'abschnitt', titel: 'Abschnitt', wert: (n) => n.abschnitt ?? '—' },
  { schluessel: 'anwendung', titel: 'Anwendung', wert: (n) => n.anwendung },
];

const FOERDERHOEHEN = new Set(['foerderhoehe_nach_abgleich', 'pumpenbetriebspunkt_mindestdruck', 'betriebspunkt_nach_abgleich', 'pumpeneinstellung']);

export function HeizungAbgleich() {
  const { api, projekt } = useOutletContext<Rahmen>();
  return <HeizungAbgleichAnsicht api={api} projektId={projekt.id} />;
}

export function HeizungAbgleichAnsicht({ api, projektId }: { api: ApiClient; projektId: string }) {
  const [daten, setDaten] = useState<AbgleichAntwort | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);

  const laden = useCallback(async () => {
    setFehler(null);
    try {
      const antwort = await api.heizungAbgleich(projektId);
      setDaten(antwort ?? null);
      if (!antwort) setFehler('Die Antwort war leer.');
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e));
    }
  }, [api, projektId]);

  useEffect(() => { void laden(); }, [laden]);

  const lauf = daten?.lauf ?? null;
  const modell = daten?.modell ?? null;

  const kopf = (
    <PageHeader
      titel="Hydraulischer Abgleich"
      beschreibung={'Einstellwerte je Verbraucher, Strangregulierventile und Differenzdruckregler, Pumpen nach dem '
        + 'Abgleich – DIN EN 14336 / VdZ-Verfahren B, gerechnet vom Lauf „heizung-abgleich“.'}
      meta={daten ? rechenstandAbgleich(lauf) : undefined}
      aktionen={(
        <ExportLeiste ziele={[
          { art: 'pdf', beschriftung: 'PDF Abgleich', laden: () => api.heizungAbgleichPdf(projektId),
            gesperrt: lauf ? undefined : { grund: 'Noch kein Abgleichlauf – erst „heizung-abgleich“ rechnen.' } },
          { art: 'excel', beschriftung: 'Excel Einstellwerte', laden: () => api.heizungAbgleichExcel(projektId),
            gesperrt: lauf ? undefined : { grund: 'Noch kein Abgleichlauf – erst „heizung-abgleich“ rechnen.' } },
        ]} />
      )}
    />
  );

  if (fehler) {
    return (
      <>
        {kopf}
        <ErrorState titel="Der hydraulische Abgleich konnte nicht geladen werden." meldung={fehler}
                    beiErneutVersuchen={() => { void laden(); }} />
      </>
    );
  }

  if (!daten) return <>{kopf}<LoadingState text="Hydraulischer Abgleich wird geladen …" /></>;

  if (!lauf || !modell) {
    return (
      <>
        {kopf}
        <EmptyState
          titel="Noch kein hydraulischer Abgleich gerechnet."
          erklaerung={'Der Abgleich entsteht mit dem Werkzeug „heizung-abgleich <Projekt> --schreiben“ nach der '
            + 'Rohrnetzberechnung, auf dem maßgebenden Heizlastlauf (Verfahren B). '
            + (daten.massgebendeHeizlast
              ? `Maßgebend ist heute Heizlast-Lauf ${daten.massgebendeHeizlast.laufnummer}.`
              : 'Heute gibt es keinen maßgebenden Heizlastlauf – dann rechnet der Abgleich nicht.')} />
        <p className="schwach"><Link to="../heizung-berechnung">Zur Rohrnetzberechnung</Link></p>
      </>
    );
  }

  const ventile = [...(modell.ventile ?? [])].sort((a, b) => a.reihenfolge - b.reihenfolge);
  const armaturen = [...(modell.armaturen ?? [])].sort((a, b) => a.reihenfolge - b.reihenfolge);
  const pruefungen = [...(modell.pruefungen ?? [])].sort((a, b) => a.reihenfolge - b.reihenfolge);
  const annahmen = modell.annahmen ?? [];
  const offen = annahmen.filter((a) => !a.bestaetigt).length;
  const gruppenzeilen = gruppen(modell);
  const schrankenGruppen = gruppenzeilen.filter((g) => g.ohneWeg > 0).length;
  const mitWert = ventile.filter((v) => v.einstellart !== 'ohne').length;
  const nicht = ventile.filter((v) => v.ergebnis === 'nicht_erfuellt').length;
  const strang = armaturen.filter((a) => a.art !== 'einzel').length;
  const regler = armaturen.filter((a) => a.art === 'dp_regler').length;
  const eeiOffen = pruefungen.filter((p) => p.regel === 'eei' && p.ergebnis === 'nicht_pruefbar').length;
  const eeiAlle = pruefungen.filter((p) => p.regel === 'eei').length;
  const schranke = (p: AbgleichPruefung) => {
    const g = gruppeVon(p);
    return FOERDERHOEHEN.has(p.regel) && g !== null && untereSchranke(modell, g);
  };

  return (
    <>
      {kopf}

      {/* Vorbehalte direkt unter dem Kopf im gemeinsamen Kasten (TX3, S1.2 Nr. 2) – seit TH-2. */}
      <Vorbehalte eintraege={[
        ...(offen > 0 ? [annahmenVorbehalt(offen, modell.annahmen.length)] : []),
        ...(schrankenGruppen > 0 ? [{ art: 'fachfrage' as const,
          text: `HZK-25: ${schrankenGruppen} Gruppe(n) mit Verbrauchern ohne gerechneten Weg (untere Schranke): Der `
            + 'ungünstigste Verbraucher kann unter ihnen sein – Förderhöhen tragen „≥“, Einstellwerte ändern sich mit dem Netz.' }] : []),
        ...(modell.heizlastVorlaeufig ? [{ art: 'sonstiges' as const,
          text: `Heizlast-Lauf ${modell.heizlastLaufnummer ?? '—'} ist nicht als maßgebend bestätigt bzw. unvollständig. `
            + 'Alle Einstellwerte stehen unter demselben Vorbehalt; Ergebnisse auf unbestätigten Eingaben heißen „vorläufig“, nie „erfüllt“.' }] : []),
        ...(daten.veraltet ? [{ art: 'veraltet' as const,
          text: 'Der Abgleich steht nicht auf dem heute maßgebenden Stand'
            + (daten.massgebendeHeizlast ? ` (Heizlast-Lauf ${daten.massgebendeHeizlast.laufnummer})` : '')
            + ' oder der Rohrnetzlauf wurde seither neu gerechnet. Mit „heizung-abgleich“ neu zu rechnen.' }] : []),
      ]} />

      <KpiReihe>
        <KpiCard titel="Verbraucher mit Einstellwert" wert={mitWert} vergleich={`von ${ventile.length}`} />
        <KpiCard titel="nicht einstellbar" wert={nicht}
                 zustand={nicht > 0 ? 'fehler' : 'gut'} zustandText={nicht > 0 ? 'nicht erfüllt' : 'keiner'}
                 vergleich="Ventil zu klein oder Rest über der Grenze" />
        <KpiCard titel="Strangarmaturen" wert={strang} vergleich={`davon ${regler} Δp-Regler`} />
        <KpiCard titel="Einzelarmaturen" wert={armaturen.length - strang} vergleich="Abgleichventil am Verbraucher" />
        <KpiCard titel="EEI nicht prüfbar" wert={eeiOffen} vergleich={`von ${eeiAlle} Pumpen · HZK-46`} />
        <KpiCard titel="unbestätigte Annahmen" wert={offen} vergleich={`von ${annahmen.length}`} />
      </KpiReihe>

      <ContentSection titel="Gruppen" beschreibung="Je Auslegungsgruppe gezählt aus der Einstellwerteliste.">
        <DataTable name="Gruppen" spalten={GRUPPEN} zeilen={gruppenzeilen} schluessel={(g) => g.gruppe}
                   suche={false} dicht ausfuhr={gruppenzeilen.length > 20} ausfuhrDateiname="abgleich-gruppen.csv"
                   leer={<EmptyState klein titel="Keine Gruppe." erklaerung="Der Rohrnetzlauf hat keine Wege." />} />
      </ContentSection>

      {BEREICHE.map((b) => {
        const teil = pruefungen.filter((p) => p.bereich === b);
        if (teil.length === 0) return null;
        return (
          <ContentSection key={b} titel={BEREICH_TITEL[b] ?? b} beschreibung={BEREICH_BESCHREIBUNG[b]}>
            <DataTable name={BEREICH_TITEL[b] ?? b} spalten={pruefspalten(schranke)} zeilen={teil}
                       schluessel={(p) => `${p.regel}-${p.reihenfolge}`} suche={teil.length > 20} dicht
                       ausfuhr={teil.length > 20} ausfuhrDateiname={`abgleich-${b}.csv`}
                       leer={<EmptyState klein titel="Keine Prüfung in diesem Bereich." />} />
          </ContentSection>
        );
      })}

      <ContentSection
        titel="Strangregulierung und Einzelarmaturen"
        beschreibung={'Vorschlag aus dem Wegbaum der Rohrnetzberechnung: Eine Armatur am Anfang eines Strangs baut ab, '
          + 'was der günstigste seiner Verbraucher zuviel hat – nur wo ein Verbraucherventil allein nicht reicht.'}
      >
        <DataTable name="Armaturen" spalten={ARMATUREN} zeilen={armaturen} schluessel={(a) => a.kennung}
                   suche={armaturen.length > 20} dicht ausfuhr={armaturen.length > 20}
                   ausfuhrDateiname="abgleich-armaturen.csv"
                   leer={<EmptyState klein titel="Keine Armatur nötig."
                                     erklaerung="Jeder Verbraucher baut seinen Abgleichdruck mit dem eigenen Ventil ab." />} />
      </ContentSection>

      <ContentSection
        titel="Einstellwerteliste je Verbraucher"
        beschreibung={'Die Monteurliste: Heizkörper mit Voreinstellung (Stufe der kv-Kennlinie), Geräte mit dem kv-Wert '
          + `des Abgleichventils. Rundung „${modell.rundung}“, Druckbasis „${modell.druckbasis}“.`}
      >
        <DataTable name="Einstellwerteliste" spalten={VENTILE} zeilen={ventile}
                   schluessel={(v) => `${v.systemgruppe}-${v.reihenfolge}`} dicht
                   ausfuhr={ventile.length > 20} ausfuhrDateiname="abgleich-einstellwerte.csv"
                   leer={<EmptyState klein titel="Keine Verbraucher." erklaerung="Der Rohrnetzlauf hat keine Wege." />} />
      </ContentSection>

      <ContentSection titel="Befunde" beschreibung="Was nicht erfüllt oder nicht prüfbar ist – mit Fachfrage.">
        <DataTable name="Befunde" spalten={BEFUNDE} zeilen={modell.befunde ?? []}
                   schluessel={(b) => `${b.schwere}-${b.regel}-${b.gegenstand}`} suche={false} dicht
                   ausfuhr={(modell.befunde ?? []).length > 20} ausfuhrDateiname="abgleich-befunde.csv"
                   leer={<EmptyState klein titel="Keine Befunde." erklaerung="Kein Ergebnis „nicht erfüllt“ oder „nicht prüfbar“." />} />
      </ContentSection>

      <ContentSection
        titel="Annahmen und Vorgaben"
        beschreibung={'Jede genutzte Vorgabe, Annahme und jeder Normwert – dieselbe Liste wie im Annahmenblatt '
          + 'des Berichts HZG-ABG. Unbestätigte stehen oben.'}
      >
        <DataTable name="Annahmen und Vorgaben" spalten={ANNAHMEN} zeilen={annahmen}
                   schluessel={(a) => a.parameter} suche={annahmen.length > 20} dicht
                   ausfuhr={annahmen.length > 20} ausfuhrDateiname="abgleich-annahmen.csv"
                   leer={<EmptyState klein titel="Keine Annahmen genutzt." />} />
      </ContentSection>

      <ContentSection titel="Grundlagen und Normbezug" beschreibung={modell.datenstand}>
        <DataTable name="Normbezug" spalten={NORMEN} zeilen={modell.normbezug ?? []}
                   schluessel={(n) => n.norm} suche={false} dicht
                   leer={<EmptyState klein titel="Kein Normbezug." />} />
        {lauf.hinweise.length > 0 && (
          <ul className="liste">
            {lauf.hinweise.map((h) => <li key={h}>{h}</li>)}
          </ul>
        )}
        <p className="schwach">
          Bericht HZG-ABG über den Knopf der Rohrnetzseite bzw. „berichte-erzeugen“ (Katalog HZG.ABG); die
          Exportleiste mit PDF und Excel folgt mit TX1/TX2. Die Rohrnetzberechnung steht auf der Seite{' '}
          <Link to="../heizung-berechnung">„Rohrnetz“</Link>.
        </p>
      </ContentSection>
    </>
  );
}
